/**
 * <FollowUpSection> — « Suivi » sur l'accueil parent (template).
 *
 * UNE carte-liste à plat, des lignes sobres : un programme de révision par ligne, le prochain
 * contrôle, le compte des autres, et « Ajouter un contrôle » en pied de carte. Ni carte ombrée,
 * ni liseré d'urgence à gauche, ni accordéon — l'urgence se lit sur la chip J-n, et le détail
 * s'ouvre dans sa feuille plutôt que de déplier l'accueil sous le doigt.
 *
 * Rien de ce que l'accordéon permettait n'est perdu : la ligne du contrôle ouvre
 * `<SchoolAssessmentDetailModal>` (chapitres, « À réviser », Modifier, Supprimer), la ligne d'un
 * programme ouvre le programme.
 *
 * Il n'y a toujours NI « chapitre en cours » NI barre `3/8` : les colonnes `ongoing` de
 * `student_chapters` ont été supprimées en 2024 et rien ne les a remplacées (`brief-accueil.md`
 * §2.5). Ce que la dernière séance a travaillé s'affiche là où c'est vrai — sur « Dernier bilan ».
 *
 * Seul le prochain contrôle est détaillé ; le reste est un compte, parce que Rapports et Activités
 * sont les surfaces où une liste a sa place.
 *
 * Les contrôles se lisent après le reste de l'accueil : tant que leur première lecture est en
 * route, la ligne du contrôle montre sa forme — « Aucun contrôle à venir » serait faux.
 */
import React from "react"

import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { CardList, CardListLinkRow, CardListRow } from "../../molecules/CardList"
import { List } from "../../molecules/List"
import { RevisionProgramRow } from "../../molecules/RevisionProgramRow"
import { SchoolAssessmentRow } from "../../molecules/SchoolAssessmentRow"
import { SchoolAssessmentRowSkeleton } from "../../molecules/SchoolAssessmentRow/SchoolAssessmentRowSkeleton"
import { type UIRevisionProgram, type UISchoolAssessment } from "../../../ports/primary/ui/types"

export type FollowUpSectionProps = {
    revisionPrograms: UIRevisionProgram[]
    assessments: UISchoolAssessment[]
    /** La première lecture des contrôles est en route : la ligne du contrôle montre sa forme. */
    assessmentsLoading?: boolean
    onOpenRevisionProgram?: (program: UIRevisionProgram) => void
    onAddAssessment?: () => void
    /** Ouvre le détail du contrôle (la feuille qui remplace l'accordéon). */
    onOpenAssessment?: (id: UISchoolAssessment["id"]) => void
    testID?: string
}

export function FollowUpSection(props: FollowUpSectionProps) {
    const {
        revisionPrograms,
        assessments,
        assessmentsLoading = false,
        onOpenRevisionProgram,
        onAddAssessment,
        onOpenAssessment,
        testID,
    } = props

    const [nextAssessment] = assessments
    const otherAssessments = Math.max(0, assessments.length - 1)

    return (
        <List title="Suivi" isEmpty={false} testID={testID}>
            <CardList>
                {revisionPrograms.map((program) => (
                    <RevisionProgramRow
                        key={program.id}
                        program={program}
                        onPress={onOpenRevisionProgram != null ? () => onOpenRevisionProgram(program) : undefined}
                    />
                ))}

                {nextAssessment != null ? (
                    <SchoolAssessmentRow
                        assessment={nextAssessment}
                        onPress={onOpenAssessment != null ? () => onOpenAssessment(nextAssessment.id) : undefined}
                    />
                ) : assessmentsLoading ? (
                    <DelayedSkeleton accessibilityLabel="Chargement des contrôles">
                        <SchoolAssessmentRowSkeleton />
                    </DelayedSkeleton>
                ) : (
                    <CardListRow>
                        <Text color="text-muted">Aucun contrôle à venir</Text>
                    </CardListRow>
                )}

                {otherAssessments > 0 && (
                    <CardListRow>
                        <Text size="text-sm" color="text-muted">
                            {otherAssessments === 1
                                ? "1 autre contrôle à venir"
                                : `${otherAssessments} autres contrôles à venir`}
                        </Text>
                    </CardListRow>
                )}

                <CardListLinkRow label="Ajouter un contrôle" iconName="plus" onPress={onAddAssessment} />
            </CardList>
        </List>
    )
}
